import { parseISO } from 'date-fns';
import { MapElement } from './map/map-element.model';
import { Point, Geometry } from 'geojson';
import { MapObject } from './map/types';

import {
  asyncScheduler,
  BehaviorSubject,
  timer,
  MonoTypeOperatorFunction,
  Observable,
} from 'rxjs'
import { filter, map, mergeMap, take } from 'rxjs/operators'

declare const window: any;

// https://stackoverflow.com/a/51365037
export type RecursivePartial<T> = {
  [P in keyof T]?:
    T[P] extends (infer U)[] ? RecursivePartial<U>[] :
    T[P] extends object ? RecursivePartial<T[P]> :
    T[P];
};

/**
 * Helper function for translations. Can be used to mark strings for translation in Typescript code
 * @param {string} s
 * @returns {string}
 * @private
 */
export function _(s: string) {
  return s;
}

/**
 * Helper function for Mapbox, returns true if browser is Firefox
 * @returns {boolean}
 */
export function fixFirefoxPrint() {
  return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
}

export function arraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false;
  }
  for (let i = arr1.length; i--;) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}

export function getDataName(tenantId: number) {
  switch (tenantId) {
    case 2:
      return '';
    case 3:
      return 'finavia';
    default:
      return 'miproData';
  }
}

export function workerBuilder(workerUrls: string | string[]): Worker {
  if (!Array.isArray(workerUrls)) {
    workerUrls = [workerUrls];
  }
  const source = 'importScripts(' + workerUrls.map(url => '"' + url + ('?cache-bust=' + Math.random()) + '"').join(',') + ');';

  let blob;
  try {
    blob = new Blob([source], { 'type': 'application/javascript' });
  } catch (e1) {
    const blobBuilder = new (window.BlobBuilder || window.WebKitBlobBuilder || window.MozBlobBuilder)();
    blobBuilder.append(source);
    blob = blobBuilder.getBlob('application/javascript');
  }
  const url = window.URL || window.webkitURL;
  const blobUrl = url.createObjectURL(blob);
  console.log('Construct worker', blobUrl);
  return new Worker(blobUrl);
}

export function strEnum<T extends string>(o: Array<T>): { [K in T]: K } {
  return o.reduce((res, key) => {
    res[key] = key;
    return res;
  }, Object.create(null));
}

/**
 * Get color code for color string
 * @param {string} key - for example, "success-base"
 * @returns {string}
 */
export function getCSSColorVariableValue(key: string): string {
  return window.getComputedStyle(document.body).getPropertyValue('--color-' + key);
}

export function parseToDate(value: string | Date | number): Date {
  if (typeof value === 'number') { // Timestamp
    return new Date(value);
  } else if (typeof value === 'object') { // Date object
    return value;
  } else { // Probably string
    const v = parseISO(value) as any;
    if (v === 'Invalid Date') {
      return new Date(parseInt(value));
    }
    return v;
  }
}

export function convertFeatureToMapObject(feature: MapElement<Geometry, Point>, layerName?: string): MapObject {
  return {
    id: feature.properties[feature.layer.metadata.uniqueBy] + '',
    layerId: feature.layer.id,
    title: layerName ?
      `${layerName}: ${feature.properties[feature.layer.metadata.labelBy]}` :
      feature.properties[feature.layer.metadata.labelBy]
  };
}

/**
 * Prevents the big red disaster recovery button ("clear cache and log out") from appearing.
 * This should be called when the application has succesfully initialized.
 */
export function preventDisasterButton(): void {
  if ((window as any).fatalButtonTimeout !== undefined) {
    console.log('Clearing fatalButtonTimeout');
    clearTimeout((window as any).fatalButtonTimeout);
    (window as any).fatalButtonTimeout = undefined;
  }

  document.getElementById('disasterButton').style.display = 'none';
}

export function cacheBust(url: string) {
  return url + '?cache-bust=' + Math.random();
}

/**
 * @see https://www.faircloth.xyz/3-ways-make-multiple-http-requests-rxjs/
 */
export function rateLimit<T>(
  count: number,
  slidingWindowTime: number,
  scheduler = asyncScheduler
): MonoTypeOperatorFunction<T> {
  let tokens = count
  const tokenChanged = new BehaviorSubject(tokens)
  const consumeToken = () => tokenChanged.next(--tokens)
  const renewToken = () => tokenChanged.next(++tokens)
  const availableTokens = tokenChanged.pipe(filter(() => tokens > 0))

  return mergeMap<T, Observable<T>>((value: T) =>
    availableTokens.pipe(
      take(1),
      map(() => {
        consumeToken()
        timer(slidingWindowTime, scheduler).subscribe(renewToken)
        return value
      })
    )
  )
}
